// Admin Settings → Evrst Webpage. Structured editor for evrststudio.com,
// matching the Evrst Printpage UX. The static HTML has `data-cms` attributes
// on every editable element + an inline loader that fetches
// /api/site-content/web_page and patches innerHTML on page load — so saves
// here go live with a refresh, no rebuild needed.
//
// For everything not covered by these fields, the bottom "Raw HTML override"
// drawer is still there for one-off edits with backup/restore.
import { useEffect, useRef, useState } from 'react'
import { useAuth } from '@workos-inc/authkit-react'
import { apiFetch } from '../../lib/api'
import { WEB_PAGE_DEFAULTS, WebPageContent, WebPageImage, mergeWebContent } from '../../lib/web-page-content'
import { useToast, ToastBanner } from '../../components/Toast'

export default function SettingsWebPage() {
  const { getAccessToken } = useAuth()
  const [c, setC] = useState<WebPageContent>(WEB_PAGE_DEFAULTS)
  const [savedAt, setSavedAt] = useState<string | null>(null)
  const [err, setErr] = useState<string | null>(null)
  const [busy, setBusy] = useState(false)
  const [showRaw, setShowRaw] = useState(false)
  const { toast, show } = useToast()

  useEffect(() => {
    (async () => {
      try {
        const d = await apiFetch<{ value: unknown; updatedAt: string | null }>(getAccessToken as unknown as () => Promise<string | undefined>, '/api/admin/settings/web_page')
        setC(mergeWebContent(d.value))
        setSavedAt(d.updatedAt)
      } catch (e) { setErr((e as Error).message) }
    })()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  async function save() {
    setBusy(true)
    try {
      await apiFetch(getAccessToken as unknown as () => Promise<string | undefined>, '/api/admin/settings/web_page', { method: 'PUT', body: JSON.stringify({ value: c }) })
      setSavedAt(new Date().toISOString())
      show('Webpage saved. Changes are live now on evrststudio.com.')
    } catch (e) {
      show((e as Error).message, 'error', 5000)
    }
    finally { setBusy(false) }
  }
  function reset() {
    if (!confirm('Reset every field to the built-in defaults? Edits will be lost on next save.')) return
    setC(JSON.parse(JSON.stringify(WEB_PAGE_DEFAULTS)))
  }

  function set<K extends keyof WebPageContent>(key: K, value: WebPageContent[K]) {
    setC((prev) => ({ ...prev, [key]: value }))
  }

  return (
    <div className="p-8">
      <ToastBanner toast={toast} />
      <div className="flex items-baseline justify-between flex-wrap gap-3 mb-6">
        <div>
          <div className="eyebrow">Settings</div>
          <h1 className="font-heading uppercase font-bold text-3xl text-ink">Evrst Webpage</h1>
          <p className="text-ink-2 text-sm mt-1 max-w-2xl">Edit content on the public <a href="https://evrststudio.com" target="_blank" rel="noreferrer" className="text-gold-dark hover:underline">evrststudio.com</a> homepage. Changes apply on next page load — the static site fetches <span className="font-mono text-xs">/api/site-content/web_page</span> and patches in your saved values.</p>
        </div>
        <div className="flex items-center gap-3">
          <span className="text-xs text-ink-3">{savedAt ? `Saved ${new Date(savedAt).toLocaleString()}` : 'Unsaved'}</span>
          <button onClick={reset} className="btn-outline text-xs">Reset</button>
          <button onClick={save} disabled={busy} className="btn-gold">{busy ? 'Saving…' : 'Save changes'}</button>
        </div>
      </div>

      {err && <div className="card text-red-700 mb-4">{err}</div>}

      <div className="space-y-3 max-w-4xl">

        <Section title="Hero">
          <Field label="Counter line (top, small caps)" value={c.hero.counter} onChange={(v) => set('hero', { ...c.hero, counter: v })} hint="Inline HTML allowed — e.g. nbsp + bullet." />
          <Grid2>
            <Field label="Headline line 1" value={c.hero.line1} onChange={(v) => set('hero', { ...c.hero, line1: v })} />
            <Field label="Headline line 2 (outline)" value={c.hero.line2} onChange={(v) => set('hero', { ...c.hero, line2: v })} />
          </Grid2>
          <Field label="Headline line 3 (gold)" value={c.hero.line3} onChange={(v) => set('hero', { ...c.hero, line3: v })} />
          <Textarea label="Description" value={c.hero.desc} onChange={(v) => set('hero', { ...c.hero, desc: v })} />
          <div className="border-t border-border pt-3 mt-3"><div className="eyebrow">Stats (×3)</div></div>
          {c.hero.stats.map((s, i) => (
            <Grid2 key={i}>
              <Field label={`Stat ${i + 1} number`} value={s.num} onChange={(v) => set('hero', { ...c.hero, stats: c.hero.stats.map((x, j) => j === i ? { ...x, num: v } : x) as typeof c.hero.stats })} />
              <Field label={`Stat ${i + 1} label`} value={s.label} onChange={(v) => set('hero', { ...c.hero, stats: c.hero.stats.map((x, j) => j === i ? { ...x, label: v } : x) as typeof c.hero.stats })} />
            </Grid2>
          ))}
          <div className="border-t border-border pt-3 mt-3"><div className="eyebrow">Hero image</div></div>
          <ImagePicker label="Hero image (the EVRST bike / hero artwork)" value={c.hero.image} onChange={(img) => set('hero', { ...c.hero, image: img })} />
        </Section>

        <Section title="About">
          <Grid2>
            <Field label="Headline line 1" value={c.about.headlineLine1} onChange={(v) => set('about', { ...c.about, headlineLine1: v })} />
            <Field label="Headline line 2 (italic)" value={c.about.headlineLine2} onChange={(v) => set('about', { ...c.about, headlineLine2: v })} hint="Italic-styled in the live page." />
          </Grid2>
          <Field label="Headline line 3" value={c.about.headlineLine3} onChange={(v) => set('about', { ...c.about, headlineLine3: v })} />
          <Textarea label="Body — paragraph 1" value={c.about.bodyP1} onChange={(v) => set('about', { ...c.about, bodyP1: v })} />
          <Textarea label="Body — paragraph 2" value={c.about.bodyP2} onChange={(v) => set('about', { ...c.about, bodyP2: v })} />
          <Grid2>
            <Field label="Award badge — number" value={c.about.awardNum} onChange={(v) => set('about', { ...c.about, awardNum: v })} />
            <Field label="Award badge — label" value={c.about.awardLabel} onChange={(v) => set('about', { ...c.about, awardLabel: v })} hint="Inline HTML allowed (<br>)." />
          </Grid2>
          <div className="border-t border-border pt-3 mt-3"><div className="eyebrow">About portrait</div></div>
          <ImagePicker label="Portrait image (currently Paul Everest)" value={c.about.image} onChange={(img) => set('about', { ...c.about, image: img })} />
        </Section>

        <Section title="Portfolio images (×12)">
          <p className="text-[11px] text-ink-3 -mt-1">The 12 tiles in the "Work" grid, in render order. Swap any image; alt text stays editable per tile.</p>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            {(Object.keys(c.portfolio) as Array<keyof WebPageContent['portfolio']>).map((slot, i) => (
              <ImagePicker
                key={slot}
                label={`Tile ${String(i + 1).padStart(2, '0')}`}
                value={c.portfolio[slot]}
                onChange={(img) => set('portfolio', { ...c.portfolio, [slot]: img })}
              />
            ))}
          </div>
        </Section>

        <Section title="Services (section header only)">
          <Grid2>
            <Field label="Eyebrow" value={c.services.eyebrow} onChange={(v) => set('services', { ...c.services, eyebrow: v })} />
            <Field label="Headline line 1" value={c.services.headlineLine1} onChange={(v) => set('services', { ...c.services, headlineLine1: v })} />
          </Grid2>
          <Field label="Headline line 2 (italic gold)" value={c.services.headlineLine2} onChange={(v) => set('services', { ...c.services, headlineLine2: v })} />
          <Textarea label="Intro" value={c.services.intro} onChange={(v) => set('services', { ...c.services, intro: v })} />
          <p className="text-[11px] text-ink-3">The six service cards (Branding / eCommerce / Product Dev / Design / Creative / Consulting) live in the raw HTML — use the override drawer below if you need to edit them.</p>
        </Section>

        <Section title="Custom Prints section">
          <Grid2>
            <Field label="Eyebrow" value={c.prints.eyebrow} onChange={(v) => set('prints', { ...c.prints, eyebrow: v })} />
            <Field label="Headline line 1" value={c.prints.headlineLine1} onChange={(v) => set('prints', { ...c.prints, headlineLine1: v })} />
          </Grid2>
          <Field label="Headline line 2 (italic gold)" value={c.prints.headlineLine2} onChange={(v) => set('prints', { ...c.prints, headlineLine2: v })} />
          <Textarea label="Intro (inline HTML allowed)" value={c.prints.intro} onChange={(v) => set('prints', { ...c.prints, intro: v })} hint="Use anchor tags etc. for inline links." />
        </Section>

        <Section title="Footer">
          <Field label="Tagline (inline HTML, e.g. <br>)" value={c.footer.tagline} onChange={(v) => set('footer', { ...c.footer, tagline: v })} />
          <Field label="Copyright" value={c.footer.copyright} onChange={(v) => set('footer', { ...c.footer, copyright: v })} />
        </Section>

        {/* Raw HTML override drawer — collapsed by default. */}
        <div className="card !p-0 overflow-hidden">
          <button onClick={() => setShowRaw((o) => !o)} className="w-full flex items-center justify-between px-5 py-3 border-b border-border text-left hover:bg-surface">
            <div className="font-heading uppercase font-bold text-sm text-ink tracking-wider">Raw HTML override (advanced)</div>
            <span className={'text-gold-dark text-xl leading-none transition-transform ' + (showRaw ? '' : '-rotate-90')}>▾</span>
          </button>
          {showRaw && <RawHtmlEditor />}
        </div>
      </div>

      <div className="sticky bottom-4 mt-6 flex justify-end max-w-4xl">
        <button onClick={save} disabled={busy} className="btn-gold shadow-lg">{busy ? 'Saving…' : 'Save changes'}</button>
      </div>
    </div>
  )
}

// Raw HTML editor — preserved as a power-user escape hatch for anything not
// surfaced in the structured fields above. Same backup/restore safety net.
interface RawState { html: string; size: number; modifiedAt: string; backups: string[] }
function RawHtmlEditor() {
  const { getAccessToken } = useAuth()
  const [data, setData] = useState<RawState | null>(null)
  const [html, setHtml] = useState('')
  const [busy, setBusy] = useState(false)

  async function load() {
    try {
      const d = await apiFetch<RawState>(getAccessToken as unknown as () => Promise<string | undefined>, '/api/admin/web-page-html')
      setData(d); setHtml(d.html)
    } catch (e) { alert((e as Error).message) }
  }
  useEffect(() => { void load() /* eslint-disable-next-line */ }, [])

  async function save() {
    if (!confirm('Save will overwrite evrststudio.com\'s index.html. A timestamped backup is created first. Continue?')) return
    setBusy(true)
    try {
      await apiFetch(getAccessToken as unknown as () => Promise<string | undefined>, '/api/admin/web-page-html', { method: 'PUT', body: JSON.stringify({ html }) })
      await load()
    } catch (e) { alert((e as Error).message) }
    finally { setBusy(false) }
  }
  async function restore(backup: string) {
    if (!confirm(`Restore from "${backup}"? The current state is backed up first.`)) return
    setBusy(true)
    try {
      await apiFetch(getAccessToken as unknown as () => Promise<string | undefined>, '/api/admin/web-page-html/restore', { method: 'POST', body: JSON.stringify({ backup }) })
      await load()
    } catch (e) { alert((e as Error).message) }
    finally { setBusy(false) }
  }

  if (!data) return <div className="p-5 text-sm text-ink-3">Loading raw HTML…</div>
  return (
    <div className="p-5 space-y-3">
      <p className="text-[11px] text-ink-3 leading-relaxed">For sections not in the structured editor (six service cards, work portfolio, testimonials, awards, contact). Every save is backed up; restore any prior version from the list.</p>
      <textarea value={html} onChange={(e) => setHtml(e.target.value)} className="w-full font-mono text-xs leading-relaxed p-3 border border-border rounded focus:outline-none focus:border-gold" style={{ minHeight: 320 }} spellCheck={false} />
      <div className="flex items-center justify-between">
        <div className="text-[11px] text-ink-3">{(data.size / 1024).toFixed(1)} KB · last edited {new Date(data.modifiedAt).toLocaleString()}</div>
        <div className="flex items-center gap-2">
          <button onClick={() => setHtml(data.html)} className="text-[11px] text-ink-3 hover:text-ink">Discard</button>
          <button onClick={save} disabled={busy || html === data.html} className="btn-gold text-xs px-3 py-1.5">{busy ? 'Saving…' : 'Save raw HTML'}</button>
        </div>
      </div>
      {data.backups.length > 0 && (
        <details className="text-xs">
          <summary className="cursor-pointer text-ink-3 hover:text-ink">{data.backups.length} backup(s) — restore</summary>
          <ul className="mt-2 space-y-1 max-h-48 overflow-y-auto">
            {data.backups.map((b) => {
              const m = b.match(/^index\.html\.bak-(\d+)/)
              const ts = m ? new Date(parseInt(m[1]!) * 1000).toLocaleString() : b
              return (
                <li key={b} className="flex items-center justify-between py-0.5">
                  <span className="text-ink-3">{ts}</span>
                  <button onClick={() => restore(b)} disabled={busy} className="text-gold-dark hover:underline">Restore</button>
                </li>
              )
            })}
          </ul>
        </details>
      )}
    </div>
  )
}

function Section({ title, children }: { title: string; children: React.ReactNode }) {
  const [open, setOpen] = useState(true)
  return (
    <div className="card !p-0 overflow-hidden">
      <button onClick={() => setOpen((o) => !o)} className="w-full flex items-center justify-between px-5 py-3 border-b border-border text-left hover:bg-surface">
        <div className="font-heading uppercase font-bold text-sm text-ink tracking-wider">{title}</div>
        <span className={'text-gold-dark text-xl leading-none transition-transform ' + (open ? '' : '-rotate-90')}>▾</span>
      </button>
      {open && <div className="p-5 space-y-3">{children}</div>}
    </div>
  )
}
function Grid2({ children }: { children: React.ReactNode }) { return <div className="grid md:grid-cols-2 gap-3">{children}</div> }
function Field({ label, value, onChange, hint }: { label: string; value: string; onChange: (v: string) => void; hint?: string }) {
  return (
    <div>
      <div className="text-[10px] uppercase tracking-wider text-ink-3 mb-1">{label}</div>
      <input value={value} onChange={(e) => onChange(e.target.value)} className="input" />
      {hint && <div className="text-[10px] text-ink-3 mt-1">{hint}</div>}
    </div>
  )
}
function Textarea({ label, value, onChange, hint }: { label: string; value: string; onChange: (v: string) => void; hint?: string }) {
  return (
    <div>
      <div className="text-[10px] uppercase tracking-wider text-ink-3 mb-1">{label}</div>
      <textarea value={value} onChange={(e) => onChange(e.target.value)} rows={3} className="input" />
      {hint && <div className="text-[10px] text-ink-3 mt-1">{hint}</div>}
    </div>
  )
}

// ImagePicker — thumbnail + "Replace image" button + editable alt text.
// Upload is base64 JSON (matches /api/files), POSTs to the admin-only
// /api/admin/web-page-image endpoint which writes the file into
// /var/www/evrststudio/uploads/ and returns the public URL.
// Resolves the preview src relative to evrststudio.com — `evrststudio.png`
// → `https://evrststudio.com/evrststudio.png`, `/uploads/foo.png` → same
// host — so the thumbnail in the admin always shows the production image.
function resolvePreviewSrc(url: string): string {
  if (!url) return ''
  if (/^https?:\/\//i.test(url)) return url
  if (url.startsWith('/')) return `https://evrststudio.com${url}`
  return `https://evrststudio.com/${url}`
}

function ImagePicker({ label, value, onChange }: { label: string; value: WebPageImage; onChange: (img: WebPageImage) => void }) {
  const { getAccessToken } = useAuth()
  const fileRef = useRef<HTMLInputElement | null>(null)
  const [busy, setBusy] = useState(false)
  const [err, setErr] = useState<string | null>(null)

  async function pick(f: File) {
    setBusy(true); setErr(null)
    try {
      const base64 = await new Promise<string>((resolve, reject) => {
        const r = new FileReader()
        r.onload = () => resolve(String(r.result || ''))
        r.onerror = () => reject(r.error ?? new Error('Could not read file'))
        r.readAsDataURL(f)
      })
      const res = await apiFetch<{ url: string; bytes: number }>(
        getAccessToken as unknown as () => Promise<string | undefined>,
        '/api/admin/web-page-image',
        {
          method: 'POST',
          body: JSON.stringify({ base64, mimeType: f.type || 'image/png', originalName: f.name }),
        },
      )
      onChange({ url: res.url, alt: value.alt })
    } catch (e) {
      setErr((e as Error).message ?? 'Upload failed')
    } finally { setBusy(false) }
  }

  return (
    <div className="rounded border border-border bg-white p-3 flex gap-3">
      <div className="w-28 h-20 shrink-0 rounded bg-surface border border-border overflow-hidden flex items-center justify-center">
        {value.url
          ? <img src={resolvePreviewSrc(value.url)} alt={value.alt} className="w-full h-full object-cover" onError={(e) => { (e.currentTarget as HTMLImageElement).style.display = 'none' }} />
          : <span className="text-ink-3 text-xs">No image</span>}
      </div>
      <div className="flex-1 min-w-0">
        <div className="text-[10px] uppercase tracking-wider text-ink-3 mb-1">{label}</div>
        <div className="flex flex-wrap items-center gap-2">
          <input ref={fileRef} type="file" accept="image/*" className="hidden" onChange={(e) => { const f = e.target.files?.[0]; if (f) pick(f); e.target.value = '' }} />
          <button type="button" onClick={() => fileRef.current?.click()} disabled={busy} className="btn-gold text-xs !px-3 !py-1.5">
            {busy ? 'Uploading…' : (value.url ? 'Replace image' : 'Upload image')}
          </button>
          <code className="text-[10px] text-ink-3 truncate max-w-[14rem]" title={value.url}>{value.url || '(none)'}</code>
        </div>
        <div className="mt-2">
          <div className="text-[10px] uppercase tracking-wider text-ink-3 mb-0.5">Alt text</div>
          <input value={value.alt} onChange={(e) => onChange({ url: value.url, alt: e.target.value })} className="input text-sm" placeholder="Describe the image for screen readers" />
        </div>
        {err && <div className="text-[11px] text-red-700 mt-1">{err}</div>}
      </div>
    </div>
  )
}
